import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AvatarSVG, DEFAULT_AVATAR } from "@/lib/avatarBuilder";

/**
 * VsHumanCountdown321 — 3-2-1 GO overlay shown right after both peers accept
 * a VS HUMANO invite and before the first question is revealed. Both peers
 * start it at (roughly) the same moment because `match_start` is broadcast
 * simultaneously to both — the small drift (<50 ms) is imperceptible.
 *
 * Rendered as a full-screen violet overlay with the two avatars flanking a
 * huge pulsing number. When `value === 0` the overlay renders "VAMOS!" for
 * a beat before the parent removes it and starts the game.
 */
export default function VsHumanCountdown321({ value, playerName, playerAvatar, opponentName, opponentAvatar }) {
  const isGo = value === 0;
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 grid place-items-center bg-gradient-to-br from-violet-700/95 via-fuchsia-700/95 to-rose-600/95 backdrop-blur-sm"
      data-testid="vsh-countdown-overlay"
    >
      <div className="text-center px-6">
        <div className="flex items-center justify-center gap-6 md:gap-10 mb-8">
          <PlayerBadge name={playerName} avatar={playerAvatar} side="left" />
          <div className="text-white/90 font-[Fredoka] font-black text-3xl md:text-5xl tracking-wider">VS</div>
          <PlayerBadge name={opponentName} avatar={opponentAvatar} side="right" />
        </div>
        <AnimatePresence mode="wait">
          <motion.div
            key={value}
            initial={{ scale: 0.4, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 1.6, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.34, 1.56, 0.64, 1] }}
            className="font-[Fredoka] font-black text-white leading-none drop-shadow-[0_10px_30px_rgba(0,0,0,0.35)]"
            style={{ fontSize: isGo ? "clamp(64px, 16vw, 200px)" : "clamp(120px, 24vw, 300px)" }}
            data-testid={`vsh-countdown-${isGo ? "go" : value}`}
          >
            {isGo ? "VAMOS!" : value}
          </motion.div>
        </AnimatePresence>
        <div className="mt-6 text-white/80 font-[Fredoka] font-bold text-sm md:text-base uppercase tracking-widest">
          Preparado? O duelo está prestes a começar
        </div>
      </div>
    </motion.div>
  );
}

function PlayerBadge({ name, avatar, side }) {
  return (
    <motion.div
      initial={{ x: side === "left" ? -40 : 40, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      transition={{ duration: 0.4 }}
      className="flex flex-col items-center gap-2"
    >
      <div className="w-20 h-20 md:w-24 md:h-24 rounded-full overflow-hidden bg-white/20 border-4 border-white shadow-xl grid place-items-center">
        <AvatarSVG cfg={avatar || DEFAULT_AVATAR} size={80} />
      </div>
      <div className="text-white font-[Fredoka] font-bold text-sm md:text-base max-w-[140px] truncate" title={name}>
        {name || "Anónimo"}
      </div>
    </motion.div>
  );
}
